import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import { FormAlert } from '@/components/feedback/FormAlert'
import { Button, FormField, Input, Modal } from '@/components/ui'
import { applyApiErrorToForm } from '@/lib/formErrors'

import { useCadastrarAparelho } from '../hooks/useAparelhosBiometria'
import {
  BiometriaNavegadorError,
  isBiometriaCancelled,
  sugerirNomeDispositivo,
} from '../services/webauthn'
import {
  aparelhoSchema,
  NOME_DISPOSITIVO_MAX,
  type AparelhoFormInput,
  type AparelhoFormValues,
} from '../types/aparelhoSchema'

const FORM_ID = 'cadastrar-aparelho-form'

/** Montado só enquanto aberto: o formulário começa limpo a cada abertura. */
export function CadastrarAparelhoModal({ onClose }: { onClose: () => void }) {
  const cadastrar = useCadastrarAparelho()
  const [generalError, setGeneralError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<AparelhoFormInput, unknown, AparelhoFormValues>({
    resolver: zodResolver(aparelhoSchema),
    defaultValues: { nomeDispositivo: sugerirNomeDispositivo() },
  })

  const onSubmit = handleSubmit(async ({ nomeDispositivo }) => {
    setGeneralError(null)
    try {
      await cadastrar.mutateAsync(nomeDispositivo)
      onClose()
    } catch (error) {
      // Leitor fechado pelo usuário: o modal fica aberto para tentar de novo, sem erro.
      if (isBiometriaCancelled(error)) return
      setGeneralError(
        error instanceof BiometriaNavegadorError
          ? error.message
          : applyApiErrorToForm(error, setError),
      )
    }
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="Cadastrar este aparelho"
      description="Depois de salvar o nome, o aparelho pede sua digital, rosto ou o bloqueio de tela."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={cadastrar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={FORM_ID} loading={cadastrar.isPending}>
            Cadastrar biometria
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormAlert message={generalError} />
        <FormField
          label="Nome do aparelho"
          htmlFor="nomeDispositivo"
          error={errors.nomeDispositivo?.message}
          required
        >
          <Input
            id="nomeDispositivo"
            autoFocus
            maxLength={NOME_DISPOSITIVO_MAX}
            placeholder="CELULAR DO TRABALHO"
            className="uppercase"
            invalid={!!errors.nomeDispositivo}
            {...register('nomeDispositivo')}
          />
        </FormField>
      </form>
    </Modal>
  )
}
